import React from 'react';
import { Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { ReportData } from '../../../types/report.types';

interface SeverityRatingPageProps {
  reportData: ReportData;
  sectionId?: string;
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: '16mm 14mm',
    paddingBottom: '22mm',
    fontFamily: 'Roboto',
    fontSize: 9.5,
    color: '#4B5563',
    position: 'relative',
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    fontSize: 8.5,
    color: '#9CA3AF',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  footerLeft: {
    textAlign: 'left',
  },
  footerRight: {
    textAlign: 'right',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0F172A',
  },
  titleUnderlineRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    width: '100%',
    height: 3,
    marginTop: 4,
    marginBottom: 14,
  },
  titleUnderlineBlue: {
    width: 60,
    height: 3,
    backgroundColor: '#2563EB',
  },
  titleUnderlineDark: {
    flex: 1,
    height: 1.2,
    backgroundColor: '#1E293B',
  },
  paragraph: {
    lineHeight: 1.60,
    marginBottom: 16,
    color: '#475569',
    fontSize: 11.5,
  },
  table: {
    borderWidth: 1.2,
    borderColor: '#CBD5E1',
    backgroundColor: '#FFFFFF',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1.8,
    borderBottomColor: '#475569',
  },
  tableHeaderCell: {
    color: '#4B5563',
    fontWeight: 'bold',
    fontSize: 9.0,
    paddingHorizontal: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    minHeight: 44,
    alignItems: 'center',
  },
  tableRowCell: {
    fontSize: 10.0,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: '#374151',
    lineHeight: 1.45,
  },
  severityPill: {
    borderRadius: 10,
    paddingVertical: 3.5,
    paddingHorizontal: 10,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-start',
    flexDirection: 'row',
    minWidth: 54,
  },
  severityPillText: {
    color: '#FFFFFF',
    fontSize: 8.5,
    fontWeight: 'bold',
    lineHeight: 1.1,
    textAlign: 'center',
  },
});

const SeverityRatingPageV2: React.FC<SeverityRatingPageProps> = ({ reportData, sectionId }) => {
  const bands = [
    { 
      sev: 'Critical', 
      range: '9.0 - 10.0', 
      color: '#DC2626', 
      desc: reportData.vulnerability_ratings?.critical || 'Trivially exploitable; full application or data compromise. Immediate action.' 
    },
    { 
      sev: 'High', 
      range: '7.0 - 8.9', 
      color: '#F97316', 
      desc: reportData.vulnerability_ratings?.high || 'Significant impact; likely exploited by a motivated attacker. Remediate urgently.' 
    },
    { 
      sev: 'Medium', 
      range: '4.0 - 6.9', 
      color: '#EAB308', 
      desc: reportData.vulnerability_ratings?.medium || 'Moderate impact or needs specific conditions. Remediate in planned cycle.' 
    },
    { 
      sev: 'Low', 
      range: '0.1 - 3.9', 
      color: '#16A34A', 
      desc: reportData.vulnerability_ratings?.low || 'Limited impact; defense-in-depth weakness. Address as resources allow.' 
    },
    { 
      sev: 'Info', 
      range: '0.0', 
      color: '#7EA1C0', 
      colorHex: '#64748B', 
      displayColor: '#849BB4', 
      desc: reportData.vulnerability_ratings?.info || 'No direct security impact; best-practice observation.' 
    },
  ];

  // Helper colors matching the mockup
  const sevColors: Record<string, string> = {
    'Critical': '#E11D48',
    'High': '#F97316',
    'Medium': '#EAB308',
    'Low': '#16A34A',
    'Info': '#8B9BB4'
  };

  return (
    <Page size="A4" id={sectionId} style={styles.page}>
      {/* Title */}
      <Text style={styles.sectionTitle}>5. Severity & Risk Rating Model</Text>
      <View style={styles.titleUnderlineRow}>
        <View style={styles.titleUnderlineBlue} />
        <View style={styles.titleUnderlineDark} />
      </View>

      {/* Sub-text */}
      <Text style={styles.paragraph}>
        Findings are rated using the <Text style={{ fontWeight: 'bold', color: '#1F2937' }}>Common Vulnerability Scoring System (CVSS v3.1 / v4.0)</Text> standard. Severity bands map to base scores as follows:
      </Text>

      {/* Table */}
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={[styles.tableHeaderCell, { width: '20%' }]}>Severity</Text>
          <Text style={[styles.tableHeaderCell, { width: '15%' }]}>CVSS</Text>
          <Text style={[styles.tableHeaderCell, { width: '65%' }]}>Definition</Text>
        </View>
        {bands.map((b, idx) => {
          const isLast = idx === bands.length - 1;
          const pillColor = sevColors[b.sev] || b.color;
          return (
            <View key={idx} style={[styles.tableRow, { borderBottomWidth: isLast ? 0 : 1 }]}>
              {/* Severity Pill */}
              <View style={[styles.tableRowCell, { width: '20%', justifyContent: 'center' }]}>
                <View style={[styles.severityPill, { backgroundColor: pillColor }]}>
                  <Text style={styles.severityPillText}>{b.sev}</Text>
                </View>
              </View>

              {/* CVSS range */}
              <Text style={[styles.tableRowCell, { width: '15%', color: '#1F2937' }]}>
                {b.range}
              </Text>

              {/* Description */}
              <Text style={[styles.tableRowCell, { width: '65%', color: '#374151' }]}>
                {b.desc}
              </Text>
            </View>
          );
        })}
      </View>
      {/* Footer page number */}
      <View style={styles.footer} fixed>
        <Text style={styles.footerLeft}>Capture The Bug - Confidential</Text>
        <Text style={styles.footerRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
      </View>
    </Page>
  );
};

export default SeverityRatingPageV2;
